import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuctionHouse } from '../types/auctionHouse.types';
import { auctionHouseService } from '../services/auctionHouse.service';

interface AuctionHouseContextType {
  currentAuctionHouse: AuctionHouse | null;
  isAggregator: boolean;
  loading: boolean;
  error: string | null;
  refreshAuctionHouse: () => Promise<void>;
}

const AuctionHouseContext = createContext<AuctionHouseContextType>({
  currentAuctionHouse: null,
  isAggregator: true,
  loading: true,
  error: null,
  refreshAuctionHouse: async () => {},
});

export const useAuctionHouse = () => useContext(AuctionHouseContext);

export const AuctionHouseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentAuctionHouse, setCurrentAuctionHouse] = useState<AuctionHouse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAuctionHouse();
  }, []);

  const loadAuctionHouse = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Detect which portal we're on
      const subdomain = auctionHouseService.getCurrentAuctionHouse();
      
      if (!subdomain) {
        // We're on the aggregator (auctionringers.com)
        setCurrentAuctionHouse(null);
      } else {
        // We're on a specific portal
        const house = await auctionHouseService.getBySubdomain(subdomain);
        
        if (!house) {
          setError(`Auction house not found: ${subdomain}`);
          setCurrentAuctionHouse(null);
        } else if (!house.isActive) {
          setError(`This auction house is currently inactive`);
          setCurrentAuctionHouse(null);
        } else {
          setCurrentAuctionHouse(house);
        }
      }
    } catch (err) {
      console.error('Error loading auction house:', err);
      setError('Failed to load auction house');
      setCurrentAuctionHouse(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAuctionHouse = async () => {
    await loadAuctionHouse();
  };

  const isAggregator = currentAuctionHouse === null && !error;

  return (
    <AuctionHouseContext.Provider 
      value={{ 
        currentAuctionHouse, 
        isAggregator, 
        loading, 
        error,
        refreshAuctionHouse 
      }}
    >
      {children}
    </AuctionHouseContext.Provider>
  );
};